import { fonts } from './fonts.js'
import { getHeadlessPool } from './headless-pool-for-board.js'
import { headlessModel } from './headless-model.js'

const protocol = 'http'
const host = 'localhost'
const path = '/internal-board-service-view'

/** headless page 에 console/error 이벤트 핸들러를 일괄 등록한다. */
function attachPageListeners(page: any) {
  page.on('console', async msg => {
    const args = await Promise.all(msg.args().map(arg => arg.jsonValue().catch(() => undefined)))

    if (args.some(a => a !== undefined)) {
      console.log(`[headless ${msg.type()}]`, ...args.filter(a => a !== undefined))
    } else {
      const text = msg.text()
      if (text) console.log(`[headless ${msg.type()}]`, text)
    }
  })

  page.on('pageerror', error => {
    console.error(`[headless pageerror] ${error.message}`)
    console.error(error.stack)
  })

  page.on('error', error => {
    console.error(`[headless fault] ${error}`)
    console.error(error.stack)
  })

  page.on('requestfailed', request => {
    console.error('Request failed:', request.url())
  })
}

export interface HeadlessRenderOptions {
  id?: string
  model?: any
  data?: any
  width?: number
  height?: number
  options?: any
  context?: any
  draft?: boolean
  /** true 이면 thumbnail 크기(400x300)로 축소 */
  isThumbnail?: boolean
}

/**
 * headless 브라우저 페이지를 준비하고 renderFn 을 실행한 결과를 반환한다.
 *
 * 공통 처리: pool acquire → headlessModel → fonts → viewport → request intercept → goto → data 주입 → renderFn → cleanup
 */
export async function withHeadlessPage<T>(
  {
    id = '',
    model = null,
    data = null,
    width: w = 0,
    height: h = 0,
    options = {} as any,
    context = {} as any,
    draft = false,
    isThumbnail = false
  }: HeadlessRenderOptions,
  renderFn: (page: any) => Promise<T>
): Promise<T | null> {
  const browser = (await getHeadlessPool().acquire()) as any
  if (!browser) return null

  const { domain, user } = context.state

  var { model: resolvedModel, base } = await headlessModel({ domain, id, model }, draft)
  const [fontsToUse, fontStyles] = await fonts(domain)

  resolvedModel.fonts = fontsToUse
  resolvedModel.fontStyles = fontStyles

  let { width, height } = resolvedModel

  if (isThumbnail) {
    const ratio = Math.min(400 / width, 300 / height)
    width = width * ratio
    height = height * ratio
  } else if (w || h) {
    const ratio = Math.min((w || width) / width, (h || height) / height)
    width = width * ratio
    height = height * ratio
  }

  width = Math.floor(w || Number(width))
  height = Math.floor(h || Number(height))

  const port = process.env.PORT ? `:${process.env.PORT}` : ''
  const url = `${protocol}://${host}${port}${path}`
  const page = await browser.newPage()

  let result: T | null = null

  try {
    await page.setViewport({ width, height })
    await page.setRequestInterception(true)
    await page.setDefaultTimeout(10000)

    attachPageListeners(page)

    const token = await user?.sign()

    page.on('request', request => {
      if (request.url() === url) {
        request.continue({
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-things-factory-domain': domain?.subdomain,
            Authorization: 'Bearer ' + token
          },
          postData: JSON.stringify({ model: resolvedModel, base })
        })
      } else if (request.url().startsWith(`${protocol}://${host}${port}`)) {
        request.continue({
          headers: {
            ...request.headers(),
            'x-things-factory-domain': domain?.subdomain,
            Authorization: 'Bearer ' + token
          }
        })
      } else {
        request.continue()
      }
    })

    await page.goto(url)

    await page.evaluate(async data => {
      if (data) {
        // @ts-ignore
        s.data = data
      }
      return new Promise(resolve => {
        // @ts-ignore
        requestAnimationFrame(() => resolve())
      })
    }, data)

    // board 내부 async 로딩 (차트 데이터, 이미지, GraphQL query 등) 정착까지
    // 대기. timeout 시 무시 (= 빠른 정적 board 영향 최소화).
    await page.waitForNetworkIdle().catch(() => {})

    result = await renderFn(page)
  } catch (error) {
    console.error(error)
  } finally {
    await page.close()
    getHeadlessPool().release(browser)
  }

  return result
}
